import { useEffect, useRef } from 'react';

export interface LightboxProps {
  /** URL картинки для полноэкранного просмотра. */
  src: string;
  /** alt для картинки (имя товара). Default '' — декоративная. */
  alt?: string;
  /** Закрытие: тап по фону, крестик, Escape. */
  onClose: () => void;
  /** aria-label кнопки закрытия. Default «Закрыть фото». */
  closeLabel?: string;
}

/**
 * Полноэкранный просмотр фото (lightbox). Тап по картинке в карточке →
 * картинка на весь экран, без обрезки (`object-contain`).
 *
 * Использует нативный `<dialog>` + `showModal()` (как VideoModal): получаем
 * встроенный focus-trap, Escape-закрытие (`cancel` event) и top-layer (рендер
 * поверх всего без z-index гонок — требование «z-50+» выполняется нативно).
 *
 * Закрытие (P-036 — это локальное состояние экрана, НЕ навигация):
 * - Крестик в правом верхнем углу (P-019 — угол, не горизонтальный ряд).
 * - Escape → нативный `cancel` event.
 * - Тап по тёмному фону вне картинки (click где `target === dialog`).
 * Картинка и крестик — прямые children диалога, поэтому клик по ним не
 * совпадает с `currentTarget` и фон не закрывается случайно.
 *
 * Async-состояний нет: картинка уже загружена в карточке (P-021 не применяется).
 */
export function Lightbox({ src, alt = '', onClose, closeLabel = 'Закрыть фото' }: LightboxProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) {
      // showModal даёт focus-trap + Escape + top-layer. В старом jsdom — no-op,
      // fallback на проставление `open` атрибута (как в VideoModal).
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', '');
      }
    }
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
    };
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр фото"
      onClick={(e) => {
        // Клик по самому диалогу (тёмный фон вокруг картинки) → закрытие.
        // Клик по картинке/крестику имеет другой target → фон не закрывается.
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 m-0 p-0 w-screen h-screen max-w-none max-h-none border-0 bg-black/90 flex items-center justify-center"
    >
      <img src={src} alt={alt} className="max-w-[100vw] max-h-[100vh] object-contain select-none" />
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="absolute top-3 right-3 w-11 h-11 min-w-tap min-h-tap flex items-center justify-center rounded-full bg-card border border-accent text-ink text-2xl leading-none focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
      >
        ×
      </button>
    </dialog>
  );
}
